import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { getBlogPosts } from '../lib/blog';
import { BlogCard } from '../components/BlogCard';
import PropFirmComparisonSection from '../components/prop/PropFirmComparisonSection';
import { getBestDiscount } from '../utils/getBestDiscount';
import HeroVideo from '../components/HeroVideo';
import HeroCarousel from '../components/home/HeroCarousel';
import homepageFirmData from '../generated/homepageFirmData';
import type { BlogPost, PropFirm } from '../types';
import { CardSkeleton } from '../components/LoadingSkeleton';

export function HomePage() {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [isBlogLoading, setIsBlogLoading] = useState(true);
  const sections: (1 | 2)[] = [1, 2];
  const firms = homepageFirmData as unknown as PropFirm[];

  const getRows = (type: 1 | 2) => {
    const sortedRows = firms
      .map((firm) => {
        const match = (firm.pricing || []).filter((price) => {
          const isRapid = price.label?.toLowerCase().includes('rapid');
          if (type === 1 && (price.phases !== 1 || isRapid)) return false;
          if (type === 2 && price.phases !== 2) return false;
          if (price.accountSize !== 100000) return false;
          const isPrimeCFD = firm.id === 'ttp' && price.name?.toLowerCase().includes('prime cfd');
          if (firm.id === 'ttp' && !isPrimeCFD) return false;
          return true;
        });

        if (!match.length) return null;
        const best = match.reduce((previous, current) => (current.fee < previous.fee ? current : previous));
        const discount = getBestDiscount(firm.discounts ?? [], best);
        return {
          ...firm,
          pricing: [best],
          discounts: discount ? [discount] : [],
        } as PropFirm;
      })
      .filter((row): row is PropFirm => Boolean(row))
      .sort((a, b) => (a.pricing?.[0]?.fee ?? 0) - (b.pricing?.[0]?.fee ?? 0));

    const displayedRows = sortedRows.slice(0, 5);
    const hyroTrader = sortedRows.find((firm) => firm.id === 'hyrotrader');
    if (hyroTrader && !displayedRows.some((firm) => firm.id === 'hyrotrader')) displayedRows.push(hyroTrader);
    return displayedRows.sort((a, b) => (a.pricing?.[0]?.fee ?? 0) - (b.pricing?.[0]?.fee ?? 0));
  };

  useEffect(() => {
    const loadPosts = async () => {
      try {
        setIsBlogLoading(true);
        const posts = await getBlogPosts();
        setBlogPosts(posts.slice(0, 3));
      } catch (error) {
        console.error('Error loading blog posts:', error);
      } finally {
        setIsBlogLoading(false);
      }
    };

    loadPosts();
  }, []);

  return (
    <>
      <Helmet>
        <title>PropViper – Risk Toolkit and Prop Firm Reviews</title>
        <meta name="description" content="Calculate a safe lot size, monitor drawdown risk and compare current prop firm rules before you trade or buy a challenge." />
        <link rel="canonical" href="https://www.propviper.com/" />
      </Helmet>

      <section className="relative overflow-hidden bg-gradient-to-br from-black via-gray-950 to-gray-900 py-10 sm:py-12 md:py-16">
        <HeroVideo />
        <div className="relative container mx-auto px-4 sm:px-6">
          <HeroCarousel />
        </div>
      </section>

      <section className="border-y border-neutral-800 bg-neutral-950 py-8 text-white sm:py-10">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="mx-auto grid max-w-6xl gap-6 rounded-3xl border border-yellow-400/25 bg-gradient-to-br from-yellow-400/10 to-neutral-950 p-5 sm:p-8 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-yellow-300">The risk toolkit for funded traders</div>
              <h1 className="mt-3 max-w-3xl text-3xl font-extrabold tracking-tight sm:text-4xl">Find the firm. Calculate the risk. Protect the account.</h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-neutral-300 sm:text-base">
                PropViper combines current prop firm reviews with a drawdown-aware lot calculator you can use before every trade.
              </p>
              <div className="mt-5 grid gap-2 text-sm text-neutral-200 sm:grid-cols-3">
                <div className="rounded-xl border border-neutral-800 bg-black/30 px-3 py-3">Safe lot size</div>
                <div className="rounded-xl border border-neutral-800 bg-black/30 px-3 py-3">Daily loss context</div>
                <div className="rounded-xl border border-neutral-800 bg-black/30 px-3 py-3">Current firm rules</div>
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
              <Link to="/tools/lot-calculator" className="inline-flex min-h-12 items-center justify-center rounded-xl bg-yellow-400 px-6 py-3 text-sm font-bold text-black transition hover:bg-yellow-300">
                Calculate safe lot size
              </Link>
              <Link to="/best-prop-firms" className="inline-flex min-h-12 items-center justify-center rounded-xl border border-neutral-700 px-6 py-3 text-sm font-semibold text-neutral-100 transition hover:border-neutral-500">
                Compare prop firms
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="bg-black py-12 text-white sm:py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="mx-auto mb-8 max-w-3xl text-center sm:mb-12">
            <h2 className="text-2xl font-bold text-yellow-400 sm:text-3xl md:text-4xl">Calculate risk in three steps</h2>
            <p className="mt-3 text-base text-neutral-300 sm:text-lg">The calculator stays simple. The workspace is optional.</p>
          </div>
          <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-3">
            {[
              ['1', 'Match the account rules', 'Enter the account balance, daily loss limit and maximum drawdown shown by the firm.'],
              ['2', 'Enter the trade', 'Add the symbol, entry and stop loss. Choose the cash or percentage risk you are prepared to lose.'],
              ['3', 'Review the survival impact', 'See lot size, cash risk, remaining equal losses and whether the position is conservative or aggressive.'],
            ].map(([number, title, body]) => (
              <article key={number} className="rounded-2xl border border-neutral-800 bg-neutral-950/50 p-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-400/10 text-lg font-bold text-yellow-400">{number}</div>
                <h3 className="mt-4 text-lg font-bold text-yellow-300">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-neutral-300">{body}</p>
              </article>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/tools/lot-calculator" className="inline-flex rounded-xl bg-yellow-400 px-5 py-3 text-sm font-semibold text-black transition hover:bg-yellow-300">Use calculator free</Link>
            <p className="mt-3 text-xs text-neutral-400">No account needed for the core calculation.</p>
          </div>
        </div>
      </section>

      <section id="comparison" className="bg-gray-950 py-12 text-white sm:py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="mx-auto mb-8 max-w-3xl text-center sm:mb-10">
            <h2 className="text-2xl font-bold text-yellow-400 sm:text-3xl md:text-4xl">Top prop trading firms compared</h2>
            <p className="mt-3 text-base text-yellow-300 sm:text-lg">Compare standardized $100K challenge pricing, drawdown and current discounts.</p>
          </div>
          <div className="space-y-10">
            {sections.map((type) => {
              const rows = getRows(type);
              return (
                <PropFirmComparisonSection
                  key={type}
                  title={`${type}-Step Evaluation`}
                  firms={rows}
                  variant="compact"
                  ctaContext="homepage"
                  bestDealFirmId={rows[0]?.id}
                  getMainPrice={(firm) => firm.pricing[0]}
                />
              );
            })}
          </div>
          <div className="mt-12 text-center sm:mt-16">
            <Link to="/compare" className="inline-flex items-center justify-center rounded-full bg-white/10 px-5 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-white/20 sm:text-base">View full comparison</Link>
          </div>
        </div>
      </section>

      <section className="bg-black py-12 pb-4 text-yellow-100 sm:py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="mx-auto mb-8 max-w-3xl text-center sm:mb-12">
            <h2 className="text-2xl font-bold text-yellow-400 sm:text-3xl md:text-4xl">Latest insights</h2>
            <p className="mt-3 text-base text-yellow-300 sm:text-lg">Trading strategies, prop firm rule breakdowns and funded-account risk guidance.</p>
          </div>
          {isBlogLoading ? (
            <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">{[1, 2, 3].map((index) => <CardSkeleton key={index} />)}</div>
          ) : blogPosts.length > 0 ? (
            <>
              <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 sm:gap-6 md:gap-8">{blogPosts.map((post) => <BlogCard key={post.slug} post={post} />)}</div>
              <div className="mb-8 text-center"><Link to="/blog" className="inline-flex items-center justify-center rounded-full bg-white/10 px-5 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-white/20 sm:text-base">View all articles</Link></div>
            </>
          ) : null}
        </div>
      </section>
    </>
  );
}

export default HomePage;
